import { useMutation } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { Form } from 'react-bootstrap';

import API from '@api/API';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCurrentUserLocation } from '@hooks/useCurrentUserLocation';

import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { Screen } from '@components/layout/Screen/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { Toast } from '@components/layout/Toast/Toast';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

const useStartInstagramOAuthMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: () => {
      return API.post<{ url: string }, {}>('/v1/instagram/oauth', {});
    },
  });

  return {
    isStartingInstagramOAuth: isPending,
    startInstagramOAuthAsync: mutateAsync,
  };
};

export const ConnectInstagramScreen: FC<{}> = () => {
  const { isStartingInstagramOAuth, startInstagramOAuthAsync } = useStartInstagramOAuthMutation();
  const { createAuthGateHandler } = useAuthGate();
  const { track } = useAnalytics();

  const handleConnectInstagram = async () => {
    track('Connect Instagram');
    createAuthGateHandler(async () => {
      const { url } = await startInstagramOAuthAsync();
      openExternalUrl(url);
    });
  };

  return (
    <>
      <Screen
        name="Connect Instagram Screen"
        hideToolbar
        headerProps={{ title: <Screen.GagglTitle /> }}
      >
        <Screen.Content>
          <TitleToolbar text="Connect Instagram" size="md" />
          <p>Connect your Instagram account to Gaggl to enable posting your calendar updates.</p>
          <div className="mt-3">
            <Button
              disabled={isStartingInstagramOAuth}
              roundness="rounded"
              startIcon={<FontAwesomeIcon faStyle="fab" name="instagram" />}
              onClick={handleConnectInstagram}
            >
              Connect Instagram
            </Button>
          </div>
        </Screen.Content>
      </Screen>
    </>
  );
};
